import { useQuery } from '@tanstack/react-query';
import { getBatteryTests } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@connectdevice/ui';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['battery-tests', 1, 100],
    queryFn: () => getBatteryTests(1, 100),
  });

  // Aggregate tests by date for the production chart
  const chartData = (() => {
    if (!data?.data) return [];
    const byDate: Record<string, number> = {};
    for (const test of data.data) {
      const date = new Date(test.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      byDate[date] = (byDate[date] ?? 0) + 1;
    }
    return Object.entries(byDate)
      .slice(-7)
      .map(([date, count]) => ({ date, count }));
  })();

  const stats = (() => {
    if (!data?.data) return { total: 0, good: 0, defect: 0, half: 0 };
    return {
      total: data.total,
      good: data.data.filter((t) => t.status === 'GOOD').length,
      defect: data.data.filter((t) => t.status === 'DEFECT').length,
      half: data.data.filter((t) => t.status === 'HALF').length,
    };
  })();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">Real-time battery testing overview</p>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard title="Total Tests" value={stats.total} color="blue" />
        <StatCard title="Passed" value={stats.good} color="green" />
        <StatCard title="Half Capacity" value={stats.half} color="yellow" />
        <StatCard title="Defective" value={stats.defect} color="red" />
      </div>

      {/* ── Production Chart ── */}
      <Card>
        <CardHeader>
          <CardTitle>Production per Day (last 7 days)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-64 flex items-center justify-center text-gray-400">Loading...</div>
          ) : (
            <ResponsiveContainer width="100%" height={256}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  color: 'blue' | 'green' | 'yellow' | 'red';
}

const textColorClasses: Record<StatCardProps['color'], string> = {
  blue: 'text-blue-600',
  green: 'text-green-600',
  yellow: 'text-yellow-600',
  red: 'text-red-600',
};

function StatCard({ title, value, color }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className={`text-3xl font-bold mt-2 ${textColorClasses[color]}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
